/**
 * Product Cockpit — fleet view (beta, super-admin).
 *
 * Every product of the selected site, one row across the whole AI value chain
 * (ingest → enrich → interpret → match → generate → publish → perform), read-only.
 *
 * Two loads on purpose:
 *   - the TABLE is server-paginated and fast (~1 s for 50 rows);
 *   - the SYSTEMIC STRIP is fleet-wide and slow (its GSC leg folds the site's whole GSC
 *     table — 6.3 s on the largest site), so it must never block the table.
 * The alternative — tallying the cards from the loaded rows — would be a count taken from
 * a display sample, which is the one thing this tool exists not to do.
 *
 * Site comes from the global SiteContext (no local selector, per the admin convention).
 *
 * Story: #5636 · Plan: docs/reports/20260902-1114-product-cockpit-admin-integration-plan.md
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import {
  fetchFleet, fetchFleetSummary,
  type FleetPage, type FleetProduct, type FleetSummary, type FleetQuery, type SummaryCard,
} from '@/services/productCockpitService';
import { SystemicStrip } from './components/SystemicStrip';
import { TooltipHost } from './components/Tooltip';
import { Legend } from './components/Legend';
import { FleetTable } from './components/FleetTable';
import { fmtInt } from './lib/stages';
import './product-cockpit.css';

const PER_PAGE = [25, 50, 100, 250];
type SortKey = NonNullable<FleetQuery['sort']>;

/** An aborted request is a cancelled one, not a failure — never surface it as an error. */
function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

export default function ProductCockpitFleet() {
  const { selectedSite, loading: siteLoading } = useSite();
  const navigate = useNavigate();
  const siteId = selectedSite?.id ?? null;

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [state, setState] = useState<'never_generated' | null>(null);
  const [sort, setSort] = useState<SortKey>('name');
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(0);

  const [fleet, setFleet] = useState<FleetPage | null>(null);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [fleetError, setFleetError] = useState<string | null>(null);

  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // debounce the search box so typing does not fire a query per keystroke
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // reset paging when the site changes
  const lastSite = useRef<string | null>(null);
  useEffect(() => {
    if (lastSite.current !== siteId) {
      lastSite.current = siteId;
      setPage(0); setQ(''); setDebouncedQ(''); setCategory(null); setState(null);
      setFleet(null); setSummary(null);
    }
  }, [siteId]);

  // ── the table ──
  useEffect(() => {
    if (!siteId) return undefined;
    const ac = new AbortController();
    setFleetLoading(true);
    setFleetError(null);
    fetchFleet(siteId, {
      limit: perPage, offset: page * perPage, q: debouncedQ || null, sort, category, state,
    }, ac.signal)
      .then((data) => { setFleet(data); setFleetLoading(false); })
      .catch((err) => {
        if (isAbort(err)) return;          // superseded by a newer request
        setFleetError(err?.message || String(err));
        setFleetLoading(false);
      });
    return () => ac.abort();
  }, [siteId, perPage, page, debouncedQ, sort, category, state]);

  // ── the fleet-wide strip (separate, slower, cached server-side) ──
  useEffect(() => {
    if (!siteId) return undefined;
    const ac = new AbortController();
    setSummaryLoading(true);
    setSummaryError(null);
    fetchFleetSummary(siteId, ac.signal)
      .then((data) => { setSummary(data); setSummaryLoading(false); })
      .catch((err) => {
        if (isAbort(err)) return;
        setSummaryError(err?.message || String(err));
        setSummaryLoading(false);
      });
    return () => ac.abort();
  }, [siteId]);

  const total = fleet?.pagination.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const start = page * perPage;

  const onSort = useCallback((key: SortKey) => { setSort(key); setPage(0); }, []);

  const onOpen = useCallback((p: FleetProduct) => {
    // the story page lands in the next PR; the route is already the contract
    navigate(`/product-cockpit/${encodeURIComponent(p.product_id)}`);
  }, [navigate]);

  // Only cards whose predicate the row filter can express EXACTLY are drillable — the
  // table then shows precisely the products the card counted, not an approximation of them.
  const onDrill = useCallback((card: SummaryCard) => {
    if (card.key !== 'never_generated') return;
    setState((cur) => (cur === 'never_generated' ? null : 'never_generated'));
    setPage(0);
  }, []);

  const categories = useMemo(
    () => (summary?.categories ?? []).filter((c) => c.category),
    [summary],
  );

  if (siteLoading) {
    return <div className="pm-cockpit"><div className="pmc-empty">Loading sites…</div></div>;
  }

  if (!siteId) {
    return (
      <div className="pm-cockpit">
        <h1 className="pmc-h1">Product Cockpit</h1>
        <div className="pmc-empty">Select a site in the header to inspect its products.</div>
      </div>
    );
  }

  return (
    <div className="pm-cockpit">
      <h1 className="pmc-h1">Product Cockpit</h1>
      <div className="pmc-sub">
        Every product of <b>{selectedSite?.name}</b> across the AI value chain — read-only
        diagnosis. <b>A red column is systemic, a red dot is one product</b>; the ring marks
        the leftmost stage that calls for action.
      </div>

      {/*
        ⛔ EVERY CROSS-SECTOR COUNT BELOW WAS COMPUTED AGAINST AN INCOMPLETE OWNERSHIP SET.
        The backend excludes a sector id it cannot read, and a SMALLER owned set makes more
        sectors look foreign — so the MATCH column and the cross-sector card read worse than
        the catalogue is. Shown here rather than left to a field nobody renders: the numbers
        stay, and what qualifies them stands beside them.
      */}
      {!!(summary?.site_sector_ids_unreadable?.length || fleet?.site_sector_ids_unreadable?.length) && (
        <div className="pmc-error" style={{ marginBottom: 12 }}>
          <div className="pmc-error-title">This site has sector ids the database cannot read</div>
          <div className="pmc-error-msg">
            {`${(summary?.site_sector_ids_unreadable ?? fleet?.site_sector_ids_unreadable ?? [])
              .map((x) => JSON.stringify(x)).join(', ')} — excluded from sites.sector_ids, so the owned set below is INCOMPLETE. `}
            Every cross-sector count and MATCH state is computed against it and reads worse
            than the catalogue is. Fix the site&apos;s sector configuration before acting on them.
          </div>
        </div>
      )}

      <SystemicStrip
        summary={summary}
        loading={summaryLoading}
        error={summaryError}
        onDrill={onDrill}
        activeKey={state === 'never_generated' ? 'never_generated' : null}
      />

      <div className="pmc-panel">
        <div className="pmc-panel-head">
          Click a row to open the product’s full story · every count states its denominator
        </div>

        <div className="pmc-toolbar">
          <input
            className="pmc-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search name, brand or product id…"
            aria-label="Search products"
          />
          <select
            className="pmc-select"
            value={category ?? ''}
            onChange={(e) => { setCategory(e.target.value || null); setPage(0); }}
            aria-label="Filter by category"
          >
            <option value="">all categories{categories.length ? ` (${categories.length})` : ''}</option>
            {categories.map((c) => (
              <option key={c.category as string} value={c.category as string}>
                {c.category} — {fmtInt(c.products)}
              </option>
            ))}
          </select>
          {category && (
            <span className="pmc-chip">
              category
              <button type="button" onClick={() => { setCategory(null); setPage(0); }} aria-label="Clear category filter">×</button>
            </span>
          )}
          {state === 'never_generated' && (
            <span className="pmc-chip">
              never generated
              <button type="button" onClick={() => { setState(null); setPage(0); }} aria-label="Clear state filter">×</button>
            </span>
          )}
          <span className="pmc-grow" />
          <span className="pmc-mute pmc-small">
            {fleetLoading && !fleet ? 'loading…' : `${fmtInt(total)} products`}
            {fleet ? ` · ${fleet._meta.computed_ms} ms · ${fleet._meta.source}` : ''}
          </span>
        </div>

        {fleetError
          ? (
            <div style={{ padding: 16 }}>
              <div className="pmc-error">
                <div className="pmc-error-title">The fleet could not be loaded</div>
                <div className="pmc-error-msg">{fleetError}</div>
              </div>
            </div>
          )
          : (
            <FleetTable
              products={fleet?.products ?? []}
              sort={sort}
              onSort={onSort}
              onOpen={onOpen}
              loading={fleetLoading}
            />
          )}

        <div className="pmc-pager">
          <button
            type="button"
            className="pmc-btn"
            disabled={page === 0 || fleetLoading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹ prev
          </button>
          <span className="pmc-small pmc-mute">page {page + 1} of {pages}</span>
          <button
            type="button"
            className="pmc-btn"
            disabled={page >= pages - 1 || fleetLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            next ›
          </button>
          <span className="pmc-grow" />
          <span className="pmc-small pmc-mute">
            {total ? `${fmtInt(start + 1)}–${fmtInt(Math.min(start + perPage, total))} of ${fmtInt(total)}` : '—'}
          </span>
          <select
            className="pmc-select"
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
            aria-label="Rows per page"
          >
            {PER_PAGE.map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>

        <div className="pmc-footnote">
          <div className="pmc-legend" style={{ marginBottom: 6 }}>
            <span><i className="pmc-dot pmc-s-good" /> good</span>
            <span><i className="pmc-dot pmc-s-partial" /> partial</span>
            <span><i className="pmc-dot pmc-s-weak" /> weak</span>
            <span><i className="pmc-dot pmc-s-poor" /> poor</span>
            <span><i className="pmc-dot pmc-s-unknown" /> unknown (not zero)</span>
            <span><i className="pmc-dot pmc-s-by_design" /> by design (not a failure)</span>
          </div>
          The systemic cards are computed over the WHOLE fleet, not over this page. Stage
          columns are per-product facts: they are not sorted or tallied here, because a
          server-paginated table could only sort and tally the rows it happens to show.
        </div>
      </div>

      <Legend />
      <TooltipHost />
    </div>
  );
}
